import { TopBar } from '../../top-bar/TopBar';
import { PublicOptions } from '../../../data/publicOptions';
import { Footer } from '../../footer/Footer';
import './Schedule.css';
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import posterEven from '../../../assets/schedule/SNN 2026-POSTER CODE-EVEN.pdf';
import posterOdd from '../../../assets/schedule/SNN 2026-POSTER CODE-ODD.pdf';
import venueMap from '../../../assets/images/MAP.jpg';

const MAP_MIN_SCALE = 1;
const MAP_MAX_SCALE = 5;
const MAP_ZOOM_STEP = 0.4;

const InteractiveImageMap = ({ src, alt }) => {
  const containerRef = useRef(null);
  const dragState = useRef({ dragging: false, startX: 0, startY: 0, originX: 0, originY: 0 });
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const clampOffset = (next, currentScale) => {
    const node = containerRef.current;
    if (!node) return next;
    const { width, height } = node.getBoundingClientRect();
    const maxX = (width * (currentScale - 1)) / 2;
    const maxY = (height * (currentScale - 1)) / 2;
    return {
      x: Math.max(-maxX, Math.min(maxX, next.x)),
      y: Math.max(-maxY, Math.min(maxY, next.y)),
    };
  };

  const applyScale = (nextScale) => {
    const clamped = Math.max(MAP_MIN_SCALE, Math.min(MAP_MAX_SCALE, nextScale));
    setScale(clamped);
    setOffset((prev) => clampOffset(prev, clamped));
  };

  const handleZoomIn = () => applyScale(scale + MAP_ZOOM_STEP);
  const handleZoomOut = () => applyScale(scale - MAP_ZOOM_STEP);
  const handleReset = () => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  };

  const handlePointerDown = (e) => {
    if (scale <= 1) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragState.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      originX: offset.x,
      originY: offset.y,
    };
  };

  const handlePointerMove = (e) => {
    if (!dragState.current.dragging) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    setOffset(
      clampOffset(
        { x: dragState.current.originX + dx, y: dragState.current.originY + dy },
        scale,
      ),
    );
  };

  const handlePointerUp = (e) => {
    dragState.current.dragging = false;
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const direction = e.deltaY < 0 ? 1 : -1;
    applyScale(scale + direction * MAP_ZOOM_STEP);
  };

  return (
    <div className="venue-map-wrapper">
      <div
        ref={containerRef}
        className={`venue-map-viewport ${scale > 1 ? 'is-zoomed' : ''}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onWheel={handleWheel}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
          }}
        />
      </div>
      <div className="venue-map-controls">
        <button type="button" onClick={handleZoomIn} aria-label="Zoom in">
          <span className="material-symbols-outlined">add</span>
        </button>
        <button type="button" onClick={handleZoomOut} aria-label="Zoom out">
          <span className="material-symbols-outlined">remove</span>
        </button>
        <button type="button" onClick={handleReset} aria-label="Reset zoom">
          <span className="material-symbols-outlined">restart_alt</span>
        </button>
      </div>
    </div>
  );
};

InteractiveImageMap.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
};

const API_BASE = 'https://nt6dwl1xpc.execute-api.us-west-1.amazonaws.com/produccion';

export const ScheduleEventCard = ({ event }) => (
  <div className="schedule-event-card">
    <p className="schedule-event-time">{event.time}</p>
    <div>
      {event.code && <span className="schedule-event-code">{event.code}</span>}
      {event.title && <p className="schedule-event-title">{event.title}</p>}
      {event.speaker && <p className="schedule-event-speaker">{event.speaker}</p>}
      {event.location && (
        <p className="schedule-event-location">
          <span className="material-symbols-outlined">location_on</span>
          {event.location}
        </p>
      )}
    </div>
  </div>
);

ScheduleEventCard.propTypes = {
  event: PropTypes.shape({
    time: PropTypes.string,
    code: PropTypes.string,
    title: PropTypes.string,
    speaker: PropTypes.string,
    location: PropTypes.string,
  }).isRequired,
};

export const ScheduleDayTabs = ({ days, activeIndex, onSelect }) => (
  <div className="schedule-days-tabs">
    {days.map((day, di) => (
      <button
        key={di}
        type="button"
        onClick={() => onSelect(di)}
        className={`schedule-day-tab ${di === activeIndex ? 'active' : ''}`}
      >
        <span className="schedule-day-tab-label">{day.dayLabel}</span>
        <span className="schedule-day-tab-date">{day.date}</span>
      </button>
    ))}
  </div>
);

ScheduleDayTabs.propTypes = {
  days: PropTypes.array.isRequired,
  activeIndex: PropTypes.number.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export const Schedule = () => {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDay, setActiveDay] = useState(0);

  useEffect(() => {
    fetch(`${API_BASE}`)
      .then((res) => res.json())
      .then((data) => {
        setSchedule(data.schedule);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const days = schedule?.days || [];
  const currentDay = days[activeDay];

  return (
    <>
      <TopBar options={PublicOptions} />
      <div className="content">
        <section className="snn-page-section">
          <h2 className="snn-title">{schedule?.title || 'Program'}</h2>

          {loading && <p className="schedule-empty">Loading schedule...</p>}

          {!loading && !schedule && (
            <p className="schedule-empty">The program will be available soon.</p>
          )}

          <div className="schedule-links-wrapper">
            <a href={posterEven} download="SNN 2026-POSTER CODE-EVEN.pdf">
              <span className="material-symbols-outlined">download</span>
              Poster Code – Even
            </a>
            <a href={posterOdd} download="SNN 2026-POSTER CODE-ODD.pdf">
              <span className="material-symbols-outlined">download</span>
              Poster Code – Odd
            </a>
          </div>

          <div className="schedule-maps-grid">
            <div className="schedule-map-wrapper">
              <h3 className="schedule-map-title">Location – CICESE, Ensenada</h3>
              <iframe
                title="CICESE, Ensenada, Baja California map"
                src="https://www.google.com/maps?q=CICESE,+Carretera+Ensenada-Tijuana+3918,+Zona+Playitas,+Ensenada,+Baja+California,+Mexico&z=15&output=embed"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
            <div className="schedule-map-wrapper">
              <h3 className="schedule-map-title">CICESE Map</h3>
              <InteractiveImageMap src={venueMap} alt="SNN 2026 venue map" />
            </div>
          </div>

          {!loading && schedule && (
            <>
              {schedule.download_links && schedule.download_links.length > 0 && (
                <div className="schedule-links-wrapper">
                  {schedule.download_links.map((link, i) => (
                    <a key={i} href={link.url} target="_blank" rel="noreferrer">
                      <span className="material-symbols-outlined">download</span>
                      {link.label}
                    </a>
                  ))}
                </div>
              )}

              {days.length > 0 && (
                <>
                  <ScheduleDayTabs
                    days={days}
                    activeIndex={activeDay}
                    onSelect={setActiveDay}
                  />

                  {currentDay && currentDay.events && currentDay.events.length > 0 ? (
                    <div className="schedule-events-list">
                      {currentDay.events.map((ev, ei) => (
                        <ScheduleEventCard key={ei} event={ev} />
                      ))}
                    </div>
                  ) : (
                    <p className="schedule-empty">No events scheduled for this day yet.</p>
                  )}
                </>
              )}
            </>
          )}
        </section>
      </div>
      <Footer />
    </>
  );
};
